'use client'

import { type Options, documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { BLOCKS, INLINES } from '@contentful/rich-text-types'
import { useCallback, useMemo } from 'react'
import styled from 'styled-components'

import { NestedFigsThemeProvider, isUppercase } from '@syconium/little-miss-figgy'
import { TypeStyle, getTypeStyle } from '@syconium/little-miss-figgy/dist/components/TypeStyle'
import { fromMd, untilMd } from '@syconium/little-miss-figgy/dist/themes/default/breakpoints'

import { gql } from '../../../../../__generated__/graphql/catalog'
import { StyledTextFragment } from '../../../../../__generated__/graphql/catalog/graphql'
import { TrackingLocationProvider } from '../../../../_providers/TrackingLocationProvider.client'

import { ContentfulStyledLink } from './ContentfulStyledLink'
import {
  createDefaultRichTextRenderMark,
  createRichTextHyperlinkRenderer,
  getContentfulRichTextPlainText,
  getNonEmptySanitizedContentfulDocument,
} from './ContentfulStyledTextRichTextUtils'

import type { Document } from '@contentful/rich-text-types'

export const styledTextQueryFragment = gql(`
  fragment StyledText on StyledText {
    __typename
    id
    titleRichText
    title
    titleTextSize
    paragraphRichText
    paragraph
    paragraphTextSize
    eyebrowRichText
    eyebrow
    textColorMobile
    textColorDesktop
    textContentMaxWidthMobile
    textContentMaxWidthDesktop
    textAlignMobile
    textAlignDesktop
    bottomMargin
    links {
      ...StyledLinkFragment
    }
  }
`)

type StyledTextElement = 'eyebrow' | 'title' | 'paragraph' | 'custom' | 'links'
type StyledTextContentField = 'eyebrow' | 'title' | 'paragraph'

type ContentfulStyledTextProps = StyledTextFragment & {
  defaultMaxWidth?: number
  defaultBottomMargin?: number
  additionalCustomContent?: React.ReactNode
  centerFloatContent?: boolean
}

const StyledTextOverlay = styled.div<{
  $textContentMaxWidthDesktop: NonNullable<ContentfulStyledTextProps['textContentMaxWidthDesktop']>
  $textContentMaxWidthMobile: NonNullable<ContentfulStyledTextProps['textContentMaxWidthMobile']>
  $textAlignMobile: ContentfulStyledTextProps['textAlignMobile']
  $textAlignDesktop: ContentfulStyledTextProps['textAlignDesktop']
  $bottomMargin: NonNullable<ContentfulStyledTextProps['bottomMargin']>
  $centerFloatContent: boolean
}>`
  position: relative;
  box-sizing: border-box;
  width: 100%;
  letter-spacing: 0.6px;
  margin-bottom: ${o => o.$bottomMargin}px;
  // TODO: Fix this auto-generated suppression.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  text-align: ${o => o.$textAlignMobile};

  .configurable-width {
    margin-left: auto;
    margin-right: auto;
  }

  ${untilMd} {
    .configurable-width {
      max-width: ${o => o.$textContentMaxWidthMobile}px;
    }

    > *,
    .configurable-width {
      ${o =>
        o.$centerFloatContent || o.$textAlignMobile !== 'left'
          ? 'margin-left: auto'
          : 'margin-left: 0px'};
      ${o =>
        o.$centerFloatContent || o.$textAlignMobile !== 'right'
          ? 'margin-right: auto'
          : 'margin-right: 0px'};
    }
  }

  ${fromMd} {
    line-height: 170%;
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    text-align: ${o => o.$textAlignDesktop};

    .configurable-width {
      max-width: ${o => o.$textContentMaxWidthDesktop}px;
    }

    > *,
    .configurable-width {
      ${o =>
        o.$centerFloatContent || o.$textAlignDesktop !== 'left'
          ? 'margin-left: auto'
          : 'margin-left: 0px'};
      ${o =>
        o.$centerFloatContent || o.$textAlignDesktop !== 'right'
          ? 'margin-right: auto'
          : 'margin-right: 0px'};
    }
  }
`

type ContentfulStyledTextTitleProps = {
  className: string
  children?: React.ReactNode
  isUppercase: boolean
  textAlignMobile: ContentfulStyledTextProps['textAlignMobile']
  textAlignDesktop: ContentfulStyledTextProps['textAlignDesktop']
  titleTextSize: ContentfulStyledTextProps['titleTextSize']
  includeBottomMargin: boolean
}

export const ContentfulStyledTextTitle = styled(
  ({ className, children, isUppercase, titleTextSize }: ContentfulStyledTextTitleProps) => {
    const typeStylesMap: Record<
      ContentfulStyledTextProps['titleTextSize'],
      ReturnType<typeof getTypeStyle>
    > = {
      h1: getTypeStyle('headlineExtraLarge'),
      h2: getTypeStyle('headlineLarge'),
      h3: getTypeStyle('headlineMedium'),
      h4: getTypeStyle('headlineSmall'),
    } as const
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const TypeStyleSelection = typeStylesMap[titleTextSize ?? 'h3']
    return (
      <TypeStyleSelection
        color='primary'
        asTag={titleTextSize}
        className={className}
        isUppercase={isUppercase}
      >
        {children}
      </TypeStyleSelection>
    )
  }
)`
  ${o => (o.includeBottomMargin ? 'margin-bottom: 16px;' : null)}
  // TODO: Fix this auto-generated suppression.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  text-align: ${o => o.textAlignMobile};

  ${fromMd} {
    display: block;
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    text-align: ${o => o.textAlignDesktop};
    ${o => (o.includeBottomMargin ? 'margin-bottom: 24px;' : null)}
  }
`

const StyledTextEyebrow = styled(TypeStyle.HeadlineExtraSmall)<{ $includeBottomMargin: boolean }>`
  font-weight: bold;
  letter-spacing: 0.15em;
  line-height: 1.25em;
  ${o => (o.$includeBottomMargin ? 'margin-bottom: 16px;' : null)}
  text-transform: uppercase;

  ${fromMd} {
    ${o => (o.$includeBottomMargin ? 'margin-bottom: 24px;' : null)}
  }
`

const StyledTextLinks = styled.div<{
  $textAlignMobile: ContentfulStyledTextProps['textAlignMobile']
  $textAlignDesktop: ContentfulStyledTextProps['textAlignDesktop']
}>`
  margin-top: ${o => o.theme.spacing(6)};
  margin-bottom: ${o => o.theme.spacing(6)};
  align-items: center;

  display: flex;
  flex-wrap: wrap;
  gap: ${o => o.theme.spacing(5)};

  text-align: ${({ $textAlignMobile }) => $textAlignMobile};

  justify-content: ${({ $textAlignMobile }) =>
    $textAlignMobile === 'left'
      ? 'flex-start'
      : $textAlignMobile === 'center'
      ? 'center'
      : // TODO: Fix this auto-generated suppression.
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      $textAlignMobile === 'right'
      ? 'flex-end'
      : 'inherit'};

  ${fromMd} {
    text-align: ${({ $textAlignDesktop }) => $textAlignDesktop};

    justify-content: ${({ $textAlignDesktop }) =>
      $textAlignDesktop === 'left'
        ? 'flex-start'
        : $textAlignDesktop === 'center'
        ? 'center'
        : // TODO: Fix this auto-generated suppression.
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        $textAlignDesktop === 'right'
        ? 'flex-end'
        : 'inherit'};
  }
`
const StyledContentfulStyledLink = styled(ContentfulStyledLink)`
  display: inline-flex;
  align-items: center;
  height: min-content;
`

const StyledTextParagraph = styled(TypeStyle.BodyDefault)<{
  $paragraphTextSize: ContentfulStyledTextProps['paragraphTextSize']
  $includeBottomMargin: boolean
}>`
  ${o => (o.$includeBottomMargin ? 'margin-bottom: 16px;' : null)}
  ${fromMd} {
    ${o => (o.$includeBottomMargin ? 'margin-bottom: 24px;' : null)}
  }
  ${o => TypeStyle.css[o.$paragraphTextSize]}
`

type StyledTextRichTextProps = {
  document: Document
  field: StyledTextContentField
  includeBottomMargin: boolean
  paragraphTextSize: ContentfulStyledTextProps['paragraphTextSize'] | null | undefined
  textAlignMobile: ContentfulStyledTextProps['textAlignMobile']
  textAlignDesktop: ContentfulStyledTextProps['textAlignDesktop']
  titleTextSize: ContentfulStyledTextProps['titleTextSize']
}

const StyledTextRichText = ({
  document,
  field,
  includeBottomMargin,
  paragraphTextSize,
  textAlignMobile,
  textAlignDesktop,
  titleTextSize,
}: StyledTextRichTextProps) => {
  // only used for determining if the text is uppercase ie: isUppercase(plainText)
  const plainText = useMemo(() => getContentfulRichTextPlainText(document), [document])
  const renderMark = useMemo(() => createDefaultRichTextRenderMark(), [])
  const renderHyperlink = useMemo(() => createRichTextHyperlinkRenderer(), [])

  const renderOptions: Options = useMemo(
    () => ({
      renderMark,
      // Contentful inserts a `\n` (via Shift+Enter) as a soft line break within a text
      // node. HTML collapses that whitespace, so convert each `\n` into an explicit <br />.
      renderText: (text: string) =>
        text
          .split('\n')
          .flatMap((segment, index) => (index > 0 ? [<br key={index} />, segment] : [segment])),
      renderNode: {
        [BLOCKS.PARAGRAPH]: (node, children) => {
          // only the document's last paragraph defers to the element-level margin rule
          const isLastNodeInDocument = node === document.content[document.content.length - 1]
          const includeBottomMarginForNode = !isLastNodeInDocument || includeBottomMargin

          if (field === 'eyebrow') {
            return (
              <StyledTextEyebrow
                color='primary'
                $includeBottomMargin={includeBottomMarginForNode}
                className='configurable-width'
                asTag='h3'
              >
                {children}
              </StyledTextEyebrow>
            )
          }

          if (field === 'title') {
            return (
              <ContentfulStyledTextTitle
                titleTextSize={titleTextSize}
                className='configurable-width'
                isUppercase={isUppercase(plainText)}
                textAlignMobile={textAlignMobile}
                textAlignDesktop={textAlignDesktop}
                includeBottomMargin={includeBottomMarginForNode}
              >
                {children}
              </ContentfulStyledTextTitle>
            )
          }

          return (
            <StyledTextParagraph
              color='primary'
              $includeBottomMargin={includeBottomMarginForNode}
              $paragraphTextSize={paragraphTextSize ?? 'bodyDefault'}
              className='configurable-width'
              asTag='p'
            >
              {children}
            </StyledTextParagraph>
          )
        },
        [INLINES.HYPERLINK]: renderHyperlink,
      },
    }),
    [
      document,
      includeBottomMargin,
      paragraphTextSize,
      plainText,
      renderHyperlink,
      renderMark,
      field,
      textAlignDesktop,
      textAlignMobile,
      titleTextSize,
    ]
  )

  return <>{documentToReactComponents(document, renderOptions)}</>
}

export const ContentfulStyledText = ({
  defaultMaxWidth = 9999,
  defaultBottomMargin = 0,
  titleRichText,
  titleTextSize,
  paragraphRichText,
  paragraphTextSize,
  eyebrowRichText,
  textColorMobile,
  textColorDesktop,
  textContentMaxWidthMobile,
  textContentMaxWidthDesktop,
  textAlignMobile,
  textAlignDesktop,
  bottomMargin,
  links,
  additionalCustomContent,
  centerFloatContent,
}: ContentfulStyledTextProps) => {
  const eyebrow = getNonEmptySanitizedContentfulDocument(eyebrowRichText)
  const title = getNonEmptySanitizedContentfulDocument(titleRichText)
  const paragraph = getNonEmptySanitizedContentfulDocument(paragraphRichText)

  const includeBottomMargin = useCallback(
    (element: StyledTextElement) => {
      const includedElements: StyledTextElement[] = []
      if (eyebrow) includedElements.push('eyebrow')
      if (title) includedElements.push('title')
      if (paragraph) includedElements.push('paragraph')
      if (additionalCustomContent) includedElements.push('custom')
      if (links.length > 0) includedElements.push('links')
      const indexOfElement = includedElements.indexOf(element)
      return indexOfElement > -1 && indexOfElement < includedElements.length - 1
    },
    [additionalCustomContent, links.length, eyebrow, paragraph, title]
  )

  // Derive desktop/mobile theme from `textColorDesktop` and `textColorMobile`
  const { themeDesktop, themeMobile } = useMemo<{
    themeDesktop: 'light' | 'dark' | 'default'
    themeMobile: 'light' | 'dark' | 'default'
  }>(() => {
    const themeDesktop =
      textColorDesktop === 'light' ? 'dark' : textColorDesktop === 'dark' ? 'light' : 'default'
    const themeMobile =
      textColorMobile === 'light' ? 'dark' : textColorMobile === 'dark' ? 'light' : 'default'

    return { themeDesktop, themeMobile }
  }, [textColorDesktop, textColorMobile])

  return (
    <StyledTextOverlay
      $textContentMaxWidthDesktop={textContentMaxWidthDesktop ?? defaultMaxWidth}
      $textContentMaxWidthMobile={textContentMaxWidthMobile ?? defaultMaxWidth}
      $textAlignMobile={textAlignMobile}
      $textAlignDesktop={textAlignDesktop}
      $bottomMargin={bottomMargin ?? defaultBottomMargin}
      $centerFloatContent={centerFloatContent ?? false}
    >
      <NestedFigsThemeProvider themeMobile={themeMobile} themeDesktop={themeDesktop}>
        {eyebrow ? (
          <StyledTextRichText
            document={eyebrow}
            field='eyebrow'
            includeBottomMargin={includeBottomMargin('eyebrow')}
            paragraphTextSize={paragraphTextSize}
            textAlignMobile={textAlignMobile}
            textAlignDesktop={textAlignDesktop}
            titleTextSize={titleTextSize}
          />
        ) : null}

        {title ? (
          <StyledTextRichText
            document={title}
            field='title'
            includeBottomMargin={includeBottomMargin('title')}
            paragraphTextSize={paragraphTextSize}
            textAlignMobile={textAlignMobile}
            textAlignDesktop={textAlignDesktop}
            titleTextSize={titleTextSize}
          />
        ) : null}

        {paragraph ? (
          <StyledTextRichText
            document={paragraph}
            field='paragraph'
            includeBottomMargin={includeBottomMargin('paragraph')}
            paragraphTextSize={paragraphTextSize}
            textAlignMobile={textAlignMobile}
            textAlignDesktop={textAlignDesktop}
            titleTextSize={titleTextSize}
          />
        ) : null}

        {additionalCustomContent}

        {links.length > 0 && (
          <StyledTextLinks
            $textAlignMobile={textAlignMobile}
            $textAlignDesktop={textAlignDesktop}
            className='configurable-width'
          >
            {links.map((link, index) => (
              <TrackingLocationProvider position={index + 1} key={index}>
                <StyledContentfulStyledLink {...link} $isUppercase={isUppercase(link.text)} />
              </TrackingLocationProvider>
            ))}
          </StyledTextLinks>
        )}
      </NestedFigsThemeProvider>
    </StyledTextOverlay>
  )
}
